/**
 * "Ouvir os microfones" (spec 016): um medidor por aparelho, por até 90 s.
 * Usado em Configurações › Ditado por voz › Microfones e no botão dos
 * microfones da barra do topo — os dois mostram o mesmo som cru.
 */
import { useEffect, useState } from 'react';
import { dbParaGanho } from '@/services/voice/mics/dsp';
import { abrirMonitor, type Monitor, type NivelAparelho } from '@/services/voice/mics/mixer';
import type { MicConfig } from '@/services/voice/mics/store';

export const OUVIR_MS = 90_000;

/** Nível do aparelho visto por esta linha (canal dividido e volume aplicados). */
export function nivelDaLinha(cfg: MicConfig, n: NivelAparelho | undefined): number {
  if (!n) return 0;
  const bruto = cfg.canal === 'L' ? (n.esq ?? n.total) : cfg.canal === 'R' ? (n.dir ?? n.total) : n.total;
  return bruto * dbParaGanho(cfg.volumeDb);
}

/**
 * Abre os medidores enquanto `ouvindo`; reabre se a lista mudar. Passados
 * 90 s chama `parar` (quem chama desliga o `ouvindo`).
 */
export function useOuvirMicrofones(ids: readonly string[], ouvindo: boolean, parar: () => void): Record<string, NivelAparelho> {
  const [niveis, setNiveis] = useState<Record<string, NivelAparelho>>({});
  const chave = ids.join('|');
  useEffect(() => {
    if (!ouvindo || !chave) return;
    let mon: Monitor | null = null;
    let fim = false;
    void abrirMonitor(chave.split('|')).then((m) => {
      if (fim) m?.parar();
      else mon = m;
    });
    const tick = setInterval(() => mon && setNiveis(mon.niveis()), 100);
    const corte = setTimeout(parar, OUVIR_MS);
    return () => {
      fim = true;
      mon?.parar();
      clearInterval(tick);
      clearTimeout(corte);
      setNiveis({});
    };
    // `parar` muda a cada render de quem chama; o corte usa o da abertura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvindo, chave]);
  return niveis;
}
